import { useLazyQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useAppContext } from "../../../../classes/contexts/AppContext";
import { usePatientContext } from "../../../../classes/contexts/PatientContext";
import { useUser } from "../../../../classes/contexts/UserContext";
import IconsEnum from "../../../../classes/enums/IconsEnum";
import { MEDICAL_CONDITIONS } from "../../../../classes/enums/MedicalConditionsEnum";
import {
  normalizeToYYYYMMDD,
  parseTimestampField,
} from "../../../../classes/utils/DateUtils";
import {
  calculateBMI,
  convertHeight,
  convertWeight,
} from "../../../../classes/utils/HealthUtils";
import Button from "../../../../components/button/Button";
import DatePickerComponent from "../../../../components/datepicker/Datepicker";
import LoadingOverlay from "../../../../components/loadingBar/LoadingOverlay";
import LabeledSelect from "../../../../components/select/LabeledSelect";
import TextArea from "../../../../components/textarea/TextArea";
import TextBox from "../../../../components/textBox/TextBox";
import ValueWithMaxInput from "../../../../components/textBox/ValueWithMaxInput";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import { useQualityOfLifeIndicatorConfigs } from "../../../../hooks/queries/useQualityOfLifeIndicatorConfigs";
import { GET_HEIGHTS_BY_IDS } from "../../../../queries/heightQueries";
import { GET_PATIENT_CONDITIONS_BY_IDS } from "../../../../queries/patientConditionQueries";
import { GET_PATIENT_NOTES_BY_IDS } from "../../../../queries/patientNoteQueries";
import { GET_WEIGHTS_BY_IDS } from "../../../../queries/weightQueries";
import {
  createClinicalHistory,
  updateClinicalHistory,
} from "../../../../services/ClinicalHistoryService";
import ModalBase from "../../../common/uploadModal/modalBase/ModalBase";
import "./ClinicalHistory.css";

const CUSTOM_QOL_OPTION_VALUE = "CUSTOM_QOL_OPTION";

const ClinicalHistoryModal = ({ existingHistory = null }) => {
  const { hidePopup } = useAppContext();
  const { refreshPatientContext, patientId, patient } = usePatientContext();
  const { userPreferences } = useUser();
  const { t } = useTranslation();
  const culture = useSelector(state => state.i18n.culture);

  // Get user's preferred units (default to kg and m if not set)
  const weightUnit =
    userPreferences?.display_settings?.weight_unit?.toLowerCase() || "kg";
  const heightUnit =
    userPreferences?.display_settings?.length_unit?.toLowerCase() || "m";
  const timeZone =
    userPreferences?.display_settings?.timezone?.toString() || "UTC";

  // Calculate date limits: max is today, min is patient's birth date
  const todayYYYYMMDD = normalizeToYYYYMMDD(new Date());
  const maxDate = todayYYYYMMDD;
  const minDate = patient?.dateOfBirth
    ? normalizeToYYYYMMDD(parseTimestampField(patient.dateOfBirth))
    : null;

  // State for form fields
  const [dateYYYYMMDD, setDateYYYYMMDD] = useState(todayYYYYMMDD);
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [notes, setNotes] = useState("");
  const [qualityIndicators, setQualityIndicators] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState("");
  const [customIndicatorName, setCustomIndicatorName] = useState("");
  const [showingCustomNameInput, setShowingCustomNameInput] = useState(false);
  const [currentValue, setCurrentValue] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Lazy queries to fetch referenced documents when editing
  const [getWeights] = useLazyQuery(GET_WEIGHTS_BY_IDS);
  const [getHeights] = useLazyQuery(GET_HEIGHTS_BY_IDS);
  const [getNotes] = useLazyQuery(GET_PATIENT_NOTES_BY_IDS);
  const [getConditions] = useLazyQuery(GET_PATIENT_CONDITIONS_BY_IDS);
  const { configs: qualityOfLifeConfigs, loading: configsLoading } =
    useQualityOfLifeIndicatorConfigs();
  const selectedConfig = useMemo(
    () =>
      qualityOfLifeConfigs.find(cfg => cfg._id === selectedConfigId) || null,
    [qualityOfLifeConfigs, selectedConfigId]
  );
  const isCustomSelection = selectedConfigId === CUSTOM_QOL_OPTION_VALUE;
  const qualityIndicatorOptions = useMemo(
    () => [
      ...qualityOfLifeConfigs.map(cfg => ({
        label: cfg.name,
        value: cfg._id,
      })),
      { label: t("324"), value: CUSTOM_QOL_OPTION_VALUE },
    ],
    [qualityOfLifeConfigs, t]
  );
  const canAddQualityIndicator = useMemo(() => {
    const hasValues = currentValue !== "" && maxValue !== "";
    if (isCustomSelection) {
      return (
        showingCustomNameInput && customIndicatorName.trim() !== "" && hasValues
      );
    }
    return !!selectedConfigId && hasValues;
  }, [
    currentValue,
    customIndicatorName,
    isCustomSelection,
    maxValue,
    selectedConfigId,
    showingCustomNameInput,
  ]);

  const parsedWeight = parseFloat(weight);
  const parsedHeight = parseFloat(height);
  const hasValidWeight = Number.isFinite(parsedWeight) && parsedWeight > 0;
  const hasValidHeight = Number.isFinite(parsedHeight) && parsedHeight > 0;
  const hasConditions = selectedConditions.length > 0;
  const hasNotes = notes.trim() !== "";
  const hasQualityIndicators = qualityIndicators.length > 0;
  const hasAtLeastOneClinicalValue =
    hasValidWeight ||
    hasValidHeight ||
    hasConditions ||
    hasNotes ||
    hasQualityIndicators;
  const weightIsMissingOrValid = weight === "" || hasValidWeight;
  const heightIsMissingOrValid = height === "" || hasValidHeight;

  const getTimeZoneOffsetMs = (date, tz) => {
    try {
      const dtf = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
      const parts = dtf.formatToParts(date).reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
      }, {});

      const asUTC = Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
        Number(parts.second)
      );
      return asUTC - date.getTime();
    } catch (err) {
      return 0;
    }
  };

  const getCurrentTimePartsInZone = tz => {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
      const parts = formatter.formatToParts(now).reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
      }, {});
      return {
        hour: Number(parts.hour ?? now.getHours()),
        minute: Number(parts.minute ?? now.getMinutes()),
        second: Number(parts.second ?? now.getSeconds()),
        millisecond: now.getMilliseconds(),
      };
    } catch (err) {
      const fallback = new Date();
      return {
        hour: fallback.getHours(),
        minute: fallback.getMinutes(),
        second: fallback.getSeconds(),
        millisecond: fallback.getMilliseconds(),
      };
    }
  };

  const buildClinicalDateWithZone = baseDate => {
    const { hour, minute, second, millisecond } = getCurrentTimePartsInZone(
      timeZone
    );

    const utcDate = Date.UTC(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      baseDate.getDate(),
      hour,
      minute,
      second,
      millisecond
    );

    const offset = getTimeZoneOffsetMs(new Date(utcDate), timeZone);
    return new Date(utcDate - offset);
  };

  // Computed BMI in real-time
  const bmi = (() => {
    if (!hasValidWeight || !hasValidHeight) return "N/A";

    // Convert to kg and m for BMI calculation
    const weightInKg = convertWeight(parsedWeight, weightUnit, "kg");
    const heightInM = convertHeight(parsedHeight, heightUnit, "m");

    return calculateBMI(weightInKg, heightInM);
  })();

  // Populate form if editing existing history
  useEffect(() => {
    const loadExistingData = async () => {
      if (existingHistory) {
        setIsLoadingData(true);

        // Set date
        if (existingHistory.createdAt) {
          const parsedDate = parseTimestampField(existingHistory.createdAt);
          if (parsedDate) {
            const yyyymmdd = normalizeToYYYYMMDD(parsedDate);
            setDateYYYYMMDD(yyyymmdd);
          }
        }

        // Fetch and populate weight
        if (existingHistory.weight) {
          try {
            const { data: weightData } = await getWeights({
              variables: { ids: [existingHistory.weight] },
            });
            if (weightData?.weightsByIds?.[0]) {
              const weightDoc = weightData.weightsByIds[0];
              // Convert from kg (stored) to user's preferred unit
              const weightValue = convertWeight(
                parseFloat(weightDoc.value),
                "kg",
                weightUnit
              );
              setWeight(weightValue.toFixed(1));
            }
          } catch (error) {
            console.error("Error loading weight:", error);
          }
        }

        // Fetch and populate height
        if (existingHistory.height) {
          try {
            const { data: heightData } = await getHeights({
              variables: { ids: [existingHistory.height] },
            });
            if (heightData?.heightsByIds?.[0]) {
              const heightDoc = heightData.heightsByIds[0];
              // Convert from m (stored) to user's preferred unit
              const heightValue = convertHeight(
                parseFloat(heightDoc.value),
                "m",
                heightUnit
              );
              setHeight(
                heightUnit === "m"
                  ? heightValue.toFixed(2)
                  : heightValue.toFixed(1)
              );
            }
          } catch (error) {
            console.error("Error loading height:", error);
          }
        }

        // Fetch and populate notes
        if (existingHistory.note) {
          try {
            const { data: noteData } = await getNotes({
              variables: { ids: [existingHistory.note] },
            });
            if (noteData?.patientNotesByIds?.[0]) {
              const noteContent = noteData.patientNotesByIds[0].content || "";
              setNotes(noteContent);
            }
          } catch (error) {
            console.error("Error loading notes:", error);
          }
        }

        // Fetch and populate conditions
        if (existingHistory.conditions) {
          try {
            const { data: conditionsData } = await getConditions({
              variables: { ids: [existingHistory.conditions] },
            });
            if (conditionsData?.patientConditionsByIds?.[0]) {
              const conditionsDoc = conditionsData.patientConditionsByIds[0];
              const conditionNames = conditionsDoc.conditions || [];
              setSelectedConditions(conditionNames);
            }
          } catch (error) {
            console.error("Error loading conditions:", error);
          }
        }

        // Populate quality of life indicators if present
        if (existingHistory.qualityOfLifeIndicatorsData?.length) {
          const mapped = existingHistory.qualityOfLifeIndicatorsData.map(
            item => {
              const matchingConfig =
                qualityOfLifeConfigs.find(
                  cfg =>
                    cfg._id === item.config_id ||
                    cfg.name?.toLowerCase() === item.name?.toLowerCase()
                ) || {};
              const fallbackId = item.name
                ? item.name.toLowerCase().replace(/\s+/g, "-")
                : `qol-${Date.now()}`;
              const derivedId =
                item._id || matchingConfig._id || item.config_id || fallbackId;

              const isCustomResolved =
                item.is_custom ||
                (!matchingConfig._id &&
                  (
                    existingHistory.custom_quality_of_life_indicators || []
                  ).includes(item._id));

              return {
                id: derivedId,
                name: item.name,
                value: item.value ?? "",
                max_value: matchingConfig.max_value ?? item.max_value ?? "",
                configId: matchingConfig._id || item.config_id || "",
                isCustom: isCustomResolved,
              };
            }
          );
          setQualityIndicators(mapped);
        }

        setIsLoadingData(false);
      }
    };

    loadExistingData();
  }, [
    existingHistory,
    weightUnit,
    heightUnit,
    getWeights,
    getHeights,
    getNotes,
    getConditions,
    qualityOfLifeConfigs,
  ]);

  // Debug: Log state changes
  useEffect(() => {
  }, [dateYYYYMMDD, weight, height, selectedConditions, notes]);

  useEffect(() => {
    if (isCustomSelection) {
      setMaxValue("");
      setShowingCustomNameInput(true);
    } else if (selectedConfig) {
      setMaxValue(
        selectedConfig.max_value !== undefined &&
          selectedConfig.max_value !== null
          ? selectedConfig.max_value.toString()
          : ""
      );
      setShowingCustomNameInput(false);
    } else {
      setMaxValue("");
      setShowingCustomNameInput(false);
    }
    setCurrentValue("");
  }, [isCustomSelection, selectedConfig]);

  const handleWeightChange = e => {
    setWeight(e.target.value);
  };

  const handleHeightChange = e => {
    setHeight(e.target.value);
  };

  const handleDateChange = yyyymmdd => {
    setDateYYYYMMDD(yyyymmdd);
  };

  const handleNotesChange = e => {
    setNotes(e.target.value);
  };

  const handleConfigChange = e => {
    const configId = e.target.value;
    setSelectedConfigId(configId);
    if (configId !== CUSTOM_QOL_OPTION_VALUE) {
      setCustomIndicatorName("");
      const config = qualityOfLifeConfigs.find(cfg => cfg._id === configId);
      if (config) {
        setMaxValue(config.max_value ?? "");
      }
    } else {
      setMaxValue("");
    }
  };

  const handleValueWithMaxChange = ({ current, max }) => {
    setCurrentValue(current);
    setMaxValue(max);
  };

  const handleAddQualityIndicator = () => {
    if (isCustomSelection) {
      const trimmedName = customIndicatorName.trim();
      if (!trimmedName) {
        alert(t("Enter a questionnaire name"));
        return;
      }

      const numericValue = parseFloat(currentValue);
      const numericMax = parseFloat(maxValue);

      if (!Number.isFinite(numericValue) || numericValue < 0) {
        alert(t("Enter a valid value"));
        return;
      }
      if (!Number.isFinite(numericMax) || numericMax <= 0) {
        alert(t("Enter a valid max value"));
        return;
      }
      if (numericValue > numericMax) {
        alert(t("Value cannot exceed max"));
        return;
      }

      const normalizedId = `custom-${trimmedName
        .toLowerCase()
        .replace(/\s+/g, "-")}`;

      const entry = {
        id: normalizedId,
        name: trimmedName,
        value: numericValue.toString(),
        max_value: numericMax.toString(),
        configId: null,
        isCustom: true,
      };

      setQualityIndicators(prev => {
        const filtered = prev.filter(item => item.id !== normalizedId);
        return [...filtered, entry];
      });
      setCustomIndicatorName("");
      setCurrentValue("");
      setMaxValue("");
      return;
    }

    const config = qualityOfLifeConfigs.find(
      cfg => cfg._id === selectedConfigId
    );
    if (!config) {
      alert(t("Select a quality of life indicator"));
      return;
    }

    const numericValue = parseFloat(currentValue);
    const numericMax = parseFloat(config.max_value ?? maxValue);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      alert(t("Enter a valid value"));
      return;
    }
    if (!Number.isFinite(numericMax) || numericMax <= 0) {
      alert(t("Enter a valid max value"));
      return;
    }
    if (numericValue > numericMax) {
      alert(t("Value cannot exceed max"));
      return;
    }

    const entry = {
      id: config._id,
      name: config.name,
      value: numericValue.toString(),
      max_value: numericMax.toString(),
      configId: config._id,
      isCustom: false,
    };

    setQualityIndicators(prev => {
      const filtered = prev.filter(item => item.configId !== config._id);
      return [...filtered, entry];
    });
    setCurrentValue("");
    setMaxValue(config.max_value ?? "");
  };

  const handleRemoveQualityIndicator = id => {
    setQualityIndicators(prev => prev.filter(item => item.id !== id));
  };

  const isFormValid = () => {
    return (
      dateYYYYMMDD &&
      weightIsMissingOrValid &&
      heightIsMissingOrValid &&
      hasAtLeastOneClinicalValue
    );
  };

  const toggleCondition = condition => {
    setSelectedConditions(prev => {
      if (prev.includes(condition)) {
        return prev.filter(c => c !== condition);
      } else {
        return [...prev, condition];
      }
    });
  };

  const handleConfirm = async () => {
    if (!isFormValid()) {
      alert(
        "Select a date and add at least one clinical detail (weight, height, conditions, notes or QoL)."
      );
      return;
    }

    if (!patientId) {
      console.error("Patient ID is missing");
      alert(t("314")); // "Failed to add clinical history. Please try again."
      return;
    }

    setIsLoading(true);

    try {
      // Convert YYYYMMDD to ISO date string using user's timezone and current time
      const parsedDate = (() => {
        if (/^\d{8}$/.test(dateYYYYMMDD)) {
          const year = dateYYYYMMDD.slice(0, 4);
          const month = dateYYYYMMDD.slice(4, 6);
          const day = dateYYYYMMDD.slice(6, 8);
          const dateOnly = new Date(`${year}-${month}-${day}T00:00:00`);
          if (!isNaN(dateOnly.getTime())) return dateOnly;
        }
        return parseTimestampField(dateYYYYMMDD);
      })();

      const clinicalDateIso = buildClinicalDateWithZone(
        parsedDate || new Date()
      ).toISOString();
      const standardIndicators = qualityIndicators.filter(
        item => !item.isCustom
      );
      const customIndicators = qualityIndicators.filter(item => item.isCustom);
      const parseNumericOrNull = val => {
        const num = parseFloat(val);
        return Number.isFinite(num) ? num : null;
      };

      const clinicalHistoryData = {
        date: clinicalDateIso,
      };

      if (!existingHistory) {
        clinicalHistoryData.patient_id = patientId;
      }

      if (hasValidWeight) {
        clinicalHistoryData.weight = convertWeight(
          parsedWeight,
          weightUnit,
          "kg"
        );
      }

      if (hasValidHeight) {
        clinicalHistoryData.height = convertHeight(
          parsedHeight,
          heightUnit,
          "m"
        );
      }

      if (hasConditions) {
        clinicalHistoryData.conditions = selectedConditions;
      }

      if (hasNotes) {
        clinicalHistoryData.note = notes.trim();
      }

      if (standardIndicators.length) {
        clinicalHistoryData.quality_of_life_indicators = standardIndicators.map(
          item => ({
            config_id: item.configId,
            name: item.name,
            value: parseFloat(item.value),
            max_value: parseNumericOrNull(item.max_value),
            is_custom: false,
          })
        );
      }

      if (customIndicators.length) {
        clinicalHistoryData.custom_quality_of_life_indicators =
          customIndicators.map(item => ({
            name: item.name,
            value: parseFloat(item.value),
            max_value: parseNumericOrNull(item.max_value),
            is_custom: true,
          }));
      }


      if (existingHistory) {
        await updateClinicalHistory(clinicalHistoryData, existingHistory._id);
      } else {
        await createClinicalHistory(clinicalHistoryData);
      }

      await refreshPatientContext();
      hidePopup();
    } catch (error) {
      console.error("Error submitting clinical history:", error);
      alert(t("314")); // "Failed to add clinical history. Please try again."
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalBase onClose={hidePopup} title={t("309")}>
      <LoadingOverlay
        isVisible={isLoadingData}
        message={t("200")}
        showProgressBar={false}
      />
      <div className="clinical-history-edit-modal">
        {/* Date Row */}
        <div className="form-section">
          <div className="form-section-title">{t("90")}</div>
          <div className="form-row">
            <DatePickerComponent
              name="date"
              handleChange={handleDateChange}
              timestamp={dateYYYYMMDD}
              placeholderText={t("302")}
              locale={culture}
              showPlaceholder={false}
              maxDate={maxDate}
              minDate={minDate}
            />
          </div>
        </div>

        {/* Weight, Height, BMI Row */}
        <div className="form-row">
          <div className="form-health-info-item">
            <div className="form-section-title">{t("303")}</div>
            <TextBox
              placeholder={`${t("303")}`}
              name="weight"
              value={weight}
              onChange={handleWeightChange}
              type="number"
              step="0.1"
              min="0"
              suffix={weightUnit}
            />
          </div>
          <div className="form-health-info-item">
            <div className="form-section-title">{t("304")}</div>
            <TextBox
              placeholder={`${t("304")}`}
              name="height"
              value={height}
              onChange={handleHeightChange}
              type="number"
              step={heightUnit === "m" ? "0.01" : "0.1"}
              min="0"
              suffix={heightUnit}
            />
          </div>
          <div className="clinical-history-bmi-display">
            <span className="bmi-label">{t("305")}</span>
            <span className="bmi-value">{bmi}</span>
          </div>
        </div>

        {/* Conditions Row */}
        <div className="form-section">
          <div className="form-section-title">{t("306")}</div>
          <div className="form-row conditions-row">
            <div className="conditions-tabs">
              {Object.entries(MEDICAL_CONDITIONS).map(([key, value]) => (
                <Button
                  key={key}
                  value={value}
                  isActive={selectedConditions.includes(value)}
                  onClick={() => toggleCondition(value)}
                  disabled={isLoading}
                  className="condition-tab"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Notes Row */}
        <div className="form-section">
          <div className="form-section-title">{t("307")}</div>
          <div className="form-row notes-row">
            <TextArea
              placeholder={t("320")}
              value={notes}
              onChange={handleNotesChange}
              name="notes"
              rows={6}
            />
          </div>
        </div>

        {/* Quality of Life Indicators */}
        <div className="form-section">
          <div className="form-section-title">Quality of Life Inidcator</div>
          <div className="form-row qol-row">
            <div className="qol-selector-slot">
              {showingCustomNameInput ? (
                <TextBox
                  placeholder="Questionnaire name"
                  value={customIndicatorName}
                  onChange={e => setCustomIndicatorName(e.target.value)}
                  name="custom-qol-name"
                  icon={IconsEnum.GOBACK}
                  onIconClick={() => {
                    setCustomIndicatorName("");
                    setSelectedConfigId("");
                    setShowingCustomNameInput(false);
                  }}
                />
              ) : (
                <LabeledSelect
                  label=""
                  items={qualityIndicatorOptions}
                  selectedItem={selectedConfigId}
                  placeholder={configsLoading ? t("200") : t("54")}
                  onChange={handleConfigChange}
                  className="qol-config-select"
                />
              )}
            </div>
            <ValueWithMaxInput
              currentValue={currentValue}
              maxValue={maxValue}
              onChange={handleValueWithMaxChange}
              currentPlaceholder="Value"
              maxPlaceholder="Max"
              maxEditable={isCustomSelection || !selectedConfig}
              disable={configsLoading}
            />
            <Button
              value="Add"
              onClick={handleAddQualityIndicator}
              disabled={configsLoading || !canAddQualityIndicator}
            />
          </div>

          {qualityIndicators.length > 0 && (
            <div className="qol-pill-list">
              {qualityIndicators.map(item => (
                <div key={item.id} className="qol-pill">
                  <span className="qol-pill-name">
                    {item.name}
                    {item.isCustom ? " (Custom)" : ""}
                  </span>
                  <span className="qol-pill-value">
                    {item.value}
                    {item.max_value ? ` / ${item.max_value}` : ""}
                  </span>
                  <button
                    className="qol-pill-remove"
                    onClick={() => handleRemoveQualityIndicator(item.id)}
                    type="button"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="button-container">
          <Button
            value={isLoading ? t("200") : t("308")}
            onClick={handleConfirm}
            disabled={!isFormValid() || isLoading}
          />
        </div>
      </div>
    </ModalBase>
  );
};

export default ClinicalHistoryModal;
